import { db } from './firebase';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import type { PortalPdfAnnotation, CommentMention } from '../types';
import { normalizeCommentReactions } from './commentReactions';

const COLLECTION = 'portalDocumentAnnotations';

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): PortalPdfAnnotation {
  const d = snap.data();
  return {
    id: snap.id,
    portalId: String(d.portalId || ''),
    dealId: String(d.dealId || ''),
    releaseId: String(d.releaseId || ''),
    bundleId: typeof d.bundleId === 'string' ? d.bundleId : undefined,
    documentId: String(d.documentId || ''),
    documentUrl: String(d.documentUrl || ''),
    pageNumber: Number(d.pageNumber) || 1,
    x: Number(d.x) || 0,
    y: Number(d.y) || 0,
    content: String(d.content || ''),
    authorUid: String(d.authorUid || ''),
    authorName: String(d.authorName || ''),
    mentions: (d.mentions as CommentMention[]) || undefined,
    reactions: normalizeCommentReactions(d.reactions),
    replies: Array.isArray(d.replies)
      ? d.replies.map(r => ({
          userId: String((r as { userId?: string }).userId || ''),
          userName: String((r as { userName?: string }).userName || ''),
          content: String((r as { content?: string }).content || ''),
          mentions: (r as { mentions?: CommentMention[] }).mentions,
          createdAt:
            (r as { createdAt?: { toDate?: () => Date } }).createdAt?.toDate?.() ?? new Date(),
        }))
      : undefined,
    createdAt: (d.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
  };
}

export function subscribePortalAnnotations(
  documentId: string,
  callback: (annotations: PortalPdfAnnotation[]) => void,
): () => void {
  const q = query(collection(db, COLLECTION), where('documentId', '==', documentId));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }))),
    err => console.warn('[portalDocumentAnnotations] subscribe failed', err),
  );
}

export async function createPortalAnnotation(
  input: Omit<PortalPdfAnnotation, 'id' | 'createdAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    ...input,
    mentions: input.mentions ?? [],
    replies: input.replies ?? [],
    createdAt: Timestamp.now(),
  });
  return ref.id;
}

export async function addPortalAnnotationReply(
  annotationId: string,
  reply: { userId: string; userName: string; content: string; mentions?: CommentMention[] },
): Promise<void> {
  const ref = doc(db, COLLECTION, annotationId);
  const snap = await (await import('firebase/firestore')).getDoc(ref);
  if (!snap.exists()) return;
  const existing = (snap.data().replies as unknown[]) || [];
  await updateDoc(ref, {
    replies: [
      ...existing,
      { ...reply, mentions: reply.mentions ?? [], createdAt: Timestamp.now() },
    ],
  });
}

export async function updatePortalAnnotationPosition(
  annotationId: string,
  x: number,
  y: number,
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, annotationId), { x, y });
}

export async function deletePortalAnnotation(annotationId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, annotationId));
}

export function portalAnnotationDocumentId(releaseId: string, fileIndex = 0): string {
  return `portal-${releaseId}${fileIndex > 0 ? `-f${fileIndex}` : ''}`;
}
